// React-free so the email/SMS preview node tests can import these directly.

import type { FieldMappingFieldItem } from '@components/text-editors/addons/field-mapping';
import {
  getVariableLabel,
  isVariableSourceMissing,
} from '@components/text-editors/addons/field-mapping-variable-label';

type AddressLike = { value: string; variables?: Record<string, FieldMappingFieldItem> };

// Matches a {{ variable }} token; capture group is the trimmed inner name.
export const VARIABLE_TOKEN_PATTERN = /\{\{\s*([^{}]+?)\s*\}\}/g;

// Splits comma-joined entries, mirroring the backend's _split_addresses.
export const splitAddresses = (entries: unknown): string[] => {
  const list = Array.isArray(entries) ? entries : entries ? [entries] : [];
  return (list as string[]).flatMap((entry) =>
    entry
      .split(',')
      .map((address) => address.trim())
      .filter(Boolean),
  );
};

// A mapped variable renders as its "Source • Field" label wrapped in {{ }} (the
// same label the editor pill shows); a plain address renders as-is.
const recipientToken = (recipient: AddressLike): string => {
  const variable = recipient.variables && Object.values(recipient.variables)[0];
  return variable ? `{{ ${getVariableLabel(variable)} }}` : recipient.value;
};

export const recipientTokens = (recipients: AddressLike[] | undefined): string[] =>
  recipients?.map(recipientToken) ?? [];

const hasBrokenRecipient = (recipients: AddressLike[] | undefined): boolean =>
  (recipients ?? []).some((recipient) => {
    const variable = recipient.variables && Object.values(recipient.variables)[0];
    return Boolean(variable && isVariableSourceMissing(variable));
  });

export const resolvedOrTemplateAddresses = (
  resolved: unknown,
  template: AddressLike[] | undefined,
): string[] => {
  const addresses = splitAddresses(resolved);
  if (addresses.length) return addresses;
  // Fall back to template chips only when a recipient is broken (keeps it red).
  // A valid recipient resolving to empty -> [] -> "Not set"; cc/bcc/reply-to hide.
  return hasBrokenRecipient(template) ? recipientTokens(template) : [];
};

export const replaceVariableTokensWithLabels = (
  text: string,
  variables: Record<string, FieldMappingFieldItem> | undefined,
): string =>
  text.replace(VARIABLE_TOKEN_PATTERN, (_, name: string) => {
    const trimmed = name.trim();
    const variable = variables?.[trimmed];
    return `{{ ${variable ? getVariableLabel(variable) : trimmed} }}`;
  });

type SubjectLike = { value?: string; variables?: Record<string, FieldMappingFieldItem> };

// Labels of broken variables across the header fields, so the preview can render
// those chips red. Keyed by the same label getVariableLabel produces for the
// token text, which is how renderWithVariableChips matches them.
export const collectBrokenVariableLabels = (
  addressLists: (AddressLike[] | undefined)[],
  subject: SubjectLike | undefined,
): string[] => {
  const labels = new Set<string>();
  const add = (variable: FieldMappingFieldItem | undefined) => {
    if (variable && isVariableSourceMissing(variable)) {
      labels.add(getVariableLabel(variable));
    }
  };
  addressLists.forEach((list) =>
    list?.forEach((recipient) => add(recipient.variables && Object.values(recipient.variables)[0])),
  );
  Object.values(subject?.variables ?? {}).forEach(add);
  return [...labels];
};

// Broken-variable labels within a single mapped field's variables map (e.g. a
// message body), so renderWithVariableChips can render those chips red.
export const brokenVariableLabels = (
  variables: Record<string, FieldMappingFieldItem> | undefined,
): string[] =>
  Object.values(variables ?? {})
    .filter(isVariableSourceMissing)
    .map((variable) => getVariableLabel(variable));

// Pass `undefined` as `resolved` for journey-level previews (no traveller data).
export const resolvedOrTemplateSubject = (
  resolved: string | undefined,
  template: SubjectLike | undefined,
): string | undefined =>
  resolved ||
  (template?.value
    ? replaceVariableTokensWithLabels(template.value, template.variables)
    : undefined);
